import { api, throwApiError } from "../../api/api";

export type StorePublic = {
  id: string;
  nomeFantasia: string;
  endereco?: string;
  estado?: string;
  /** Só nas respostas de criação e edição. */
  costCenterIds?: string[];
  /** Só na leitura por id (`getStoreByIdApi`): preenchida na loja desativada. */
  deletedAt?: string | null;
};

export type CreateStoreRequest = {
  nomeFantasia: string;
  endereco?: string;
  estado?: string;
  /** Centros de custo que agrupam a loja. */
  costCenterIds?: string[];
};

export type UpdateStoreRequest = {
  nomeFantasia?: string;
  endereco?: string;
  estado?: string;
  /** Substitui a lista anterior; [] desvincula todos. Ausente = não mexe. */
  costCenterIds?: string[];
};

export type PaginatedStoresResponse = {
  items: StorePublic[];
  page: number;
  limit: number;
  total: number;
};

export type GetAvailableStoresParams = {
  from?: string;
  to?: string;
};

export type GetStoresPaginatedParams = {
  page?: number;
  limit?: number;
  nome?: string;
  all?: boolean;
  status?: "active" | "deleted" | "all";
};

export const createStoreApi = async (
  dto: CreateStoreRequest
): Promise<StorePublic> => {
  try {
    const { data } = await api.post<StorePublic>("/stores", dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao criar loja.");
    throw error;
  }
};

export const updateStoreByIdApi = async (
  id: string,
  dto: UpdateStoreRequest
): Promise<StorePublic> => {
  try {
    const { data } = await api.patch<StorePublic>(`/stores/${id}`, dto);
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao atualizar loja.");
    throw error;
  }
};

export const deactivateStoreByIdApi = async (
  id: string
): Promise<StorePublic> => {
  try {
    const { data } = await api.patch<StorePublic>(`/stores/${id}/deactivate`, {});
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao desativar loja.");
    throw error;
  }
};

export const reactivateStoreByIdApi = async (
  id: string
): Promise<StorePublic> => {
  try {
    const { data } = await api.patch<StorePublic>(`/stores/${id}/reactivate`, {});
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao reativar loja.");
    throw error;
  }
};

export const getStoresPaginatedApi = async (
  token: string,
  { page = 1, limit = 10, nome, all = false, status }: GetStoresPaginatedParams = {}
): Promise<PaginatedStoresResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<PaginatedStoresResponse>("/stores", {
      params: {
        ...(all ? {} : { page, limit }),
        ...(nome ? { nome } : {}),
        ...(all ? { all } : {}),
        ...(status ? { status } : {}),
      },
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar lojas.");
    throw error;
  }
};

export const getMyStoresPaginatedApi = async (
  token: string,
  page = 1,
  limit = 10
): Promise<PaginatedStoresResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<PaginatedStoresResponse>("/stores/me", {
      params: { page, limit },
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar suas lojas.");
    throw error;
  }
};

export const getAvailableStoresPaginatedApi = async (
  token: string,
  page = 1,
  limit = 10,
  params: GetAvailableStoresParams = {}
): Promise<PaginatedStoresResponse> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<PaginatedStoresResponse>("/stores/available", {
      params: {
        page,
        limit,
        ...(params.from ? { from: params.from } : {}),
        ...(params.to ? { to: params.to } : {}),
      },
      headers: { Authorization: `Bearer ${rawToken}` },
    });

    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar lojas com horários disponíveis.");
    throw error;
  }
};

export const getStoreByIdApi = async (
  token: string,
  id: string
): Promise<StorePublic> => {
  const rawToken = token?.startsWith("Bearer ") ? token.slice(7) : token;

  try {
    const { data } = await api.get<StorePublic>(`/stores/${id}`, {
      headers: { Authorization: `Bearer ${rawToken}` },
    });
    return data;
  } catch (error) {
    throwApiError(error, "Erro ao buscar loja.");
    throw error;
  }
};
